import { filterByCategoryType } from '@/utils/playsFiltering';
import plays from '@/data/plays';
import {
  getPlayerInfo,
  getPlayersBySeasonAndTeam,
  getPlayerStatisticsBySeason,
  getGoalsPlayerBySeason,
  getPlayerInfoAndSeasons,
} from '@/utils/api/players';
import playerGoalPlayIndexes from '@/data/player/playerGoalPlayIndexes';

function getFormattedSeasons(seasonsObject) {
  return Object.entries(seasonsObject).reduce((accumulator, [k, v]) => {
    if (v.show_videos) {
      accumulator[k] = {
        name: v.name,
      };
    }
    // accumulator[k] = {
    //   name: v,
    // };
    return accumulator;
  }, {});
}

const state = {
  player: {},
  seasonPlayers: [],
  currentTeamPlayers: [],
  playerSeasons: {
    0: {},
    1: {},
  },
  playsGoal: plays.playsGoal,
  plays: plays.plays,
  playerTeamClass: null,
  lastPlayersForPlayersView: [],
  lastTeamForPlayersView: 0,
  lastTeamNameForPlayersView: '',
  lastSeasonForSeasonPlayers: 0,
  lastSeasonPlayers: [],
  loadCount: 0,
};

const mutations = {
  setLoadCount(state, loadCount) {
    state.loadCount = loadCount;
  },
  updateSeasonPlayers(state, playersByTeam) {
    state.lastSeasonPlayers = playersByTeam;
    state.seasonPlayers = playersByTeam.reduce((accumulator, team) => {
      accumulator.push(...team.players);
      return accumulator;
    }, []);
  },
  updatePlayerSeasons(state, { teamClass = 0, seasons = {} }) {
    state.playerSeasons[teamClass] = seasons;
  },
  updatePlayer(state, playerInfo) {
    state.player = playerInfo;
  },
  updateCurrentTeamPlayers(state, players) {
    state.currentTeamPlayers = players;
  },
  updatePlayerTeamClass(state, teamClass) {
    state.playerTeamClass = teamClass;
  },
  updateTeamForPlayersView(state, teamId) {
    state.lastTeamForPlayersView = teamId;
  },
  updateTeamNameForPlayersView(state, name) {
    state.lastTeamNameForPlayersView = name;
  },
  updatePlayersForPlayersView(state, players) {
    state.lastPlayersForPlayersView = players;
  },
  updateLastSeasonForSeasonPlayers(state, seasonId) {
    state.lastSeasonForSeasonPlayers = seasonId;
  },
};

const actions = {
  /**
   * @author Cristhian Gomez
   * @description This action cleans baseplayerstack
   * @return {void}
   */
  cleanData({ commit }) {
    commit('playersPossession/updatePlaysStack', [], { root: true });
  },
  /**
   * @author Alejandro
   * @description This action is called when a user opens player analysis, and checks the data
   * required in the storage.
   * @param {Object} context
   * @param {number} playerId - The team id provided by the url playersAnalysis/:seasonId/:teamId
   * @param {number} seasonId - The season id provided by the url playersAnalysis/:seasonId/:teamId
   * @return {Promise<void>}
   */
  async checkState({ commit, dispatch, rootGetters }, { seasonId, playerId }) {
    try {
      commit('setIsSectionLoading', true, { root: true });
      commit('updatePlayerTeamClass', null);
      commit('updatePlayer', { player_id: playerId });
      commit('filterGeneral/setSeasonId', seasonId || rootGetters['loginGeneral/seasonId'], { root: true });
      const promises = [
        getPlayerInfoAndSeasons({
          seasonId: seasonId || rootGetters['loginGeneral/seasonId'],
          playerId,
        }),
      ];
      await dispatch('fillStatisticData', { teamClass: 0, promises });
    } catch (e) {
      // commit('setIsSectionLoading', false, { root: true });
      throw new Error(e);
    }
  },
  /**
   * @author Alejandro
   * @description This action is called when a user opens player analysis, and checks the data
   * required in the storage.
   * @param {Object} context
   * @param {number} playerId - The team id provided by the url playersAnalysis/:seasonId/:teamId
   * @param {number} seasonId - The season id provided by the url playersAnalysis/:seasonId/:teamId
   * @return {Promise<void>}
   */
  async checkStateSummary({ commit, dispatch }, { seasonNumber, playerId }) {
    try {
      commit('setIsSectionLoading', true, { root: true });
      commit('updatePlayerTeamClass', null);
      commit('updatePlayer', { player_id: playerId });
      // commit('filterGeneral/setSeasonId', seasonNumber || rootGetters['loginGeneral/seasonId'], { root: true });
      const promises = [
        getPlayerInfoAndSeasons({
          seasonId: seasonNumber,
          playerId,
        }),
      ];
      await dispatch('fillStatisticDataSummary', { teamClass: 0, promises });
    } catch (e) {
      // commit('setIsSectionLoading', false, { root: true });
      throw new Error(e);
    }
  },

  /**
   * @author Alejandro
   * @description This function fill the statistic data for player, is triggered by the action 'checkState'
   * or by the view Player.vue when a season changes or the team changes from club to national
   * @param dispatch
   * @param commit
   * @param state
   * @param rootGetters
   * @param {number} teamClass - 1 for national team and 0 for club
   * @param {Object} payload
   * @return {Promise<never>}
   */
  async fillStatisticDataSummary(
    { dispatch, commit, state, rootGetters, rootState },
    payload = { teamClass: 0, promises: [undefined] },
  ) {
    try {
      state.loadCount += 1;
      if (!rootState.isSectionLoading) commit('setIsSectionLoading', true, { root: true });
      payload.promises.push(dispatch('getPlayersBySeason', true));
      payload.promises.push(dispatch('teamsGeneral/getTeamsBySeasonForAnalysis', true, { root: true }));
      payload.promises.push(
        dispatch('gamesGeneral/fetchGamesBySeason', rootGetters['filterGeneral/getSeasonId'], { root: true }),
      );
      payload.promises.push(dispatch('leagueGeneral/getSeasonPlays', true, { root: true }));
      payload.promises.push(
        getGoalsPlayerBySeason({
          seasonId: rootGetters['filterGeneral/getSeasonId'],
          playerId: state.player.player_id,
        }),
      );
      payload.promises.push(
        getPlayerStatisticsBySeason({
          seasonId: rootGetters['filterGeneral/getSeasonId'],
          playerId: state.player.player_id,
        }),
      );
      const [
        playerInfoAndSeasons,
        seasonPlayers,
        seasonTeams,
        seasonWithGames,
        seasonPlays,
        {
          data: { data_combination },
        },
        {
          data: {
            data: { posession, offensive, defensive },
          },
        },
      ] = await Promise.all(payload.promises);
      const seasonGames = seasonWithGames.games || [];
      if (playerInfoAndSeasons) {
        // playerInfoAndSeasons.seasons = Object.entries(playerInfoAndSeasons.seasons).reduce((accumulator, [k, v]) => {
        //   if (seasonsAllowed.has(Number(k))) {
        //     accumulator[k] = v;
        //   }
        //   return accumulator;
        // }, {});
        // playerInfoAndSeasons.hasPlayerClubSeasons = !!Object.keys(playerInfoAndSeasons.seasons).length;
        // commit('filterGeneral/setHasPlayerClubSeasons', playerInfoAndSeasons.hasPlayerClubSeasons, {
        //   root: true,
        // });
        // commit('filterGeneral/setHasPlayerNationalTeam', playerInfoAndSeasons.is_national_team, { root: true });
        // commit('filterGeneral/setTeamId', playerInfoAndSeasons.team_id, { root: true });
        // commit(
        //   'mediaGeneral/setSummaryData',
        //   {
        //     isTeam: false,
        //     subjectId: playerInfoAndSeasons.player_id,
        //     teamId: playerInfoAndSeasons.team_id,
        //     positionId: playerInfoAndSeasons.position_id,
        //   },
        //   { root: true },
        // );
        // commit('updatePlayerSeasons', {
        //   teamClass: 0,
        //   seasons: getFormattedSeasons({ ...playerInfoAndSeasons.seasons }),
        // });
        // commit('updatePlayerSeasons', {
        //   teamClass: 1,
        //   seasons: getFormattedSeasons({ ...playerInfoAndSeasons.national_seasons }),
        // });
        // const playerTeam = seasonTeams.find(team => team.team_id === playerInfoAndSeasons.team_id);
        // if (playerTeam) {
        //   playerInfoAndSeasons.teamName = playerTeam.colloquial_name || playerTeam.team_name;
        // }
        commit('updatePlayer', playerInfoAndSeasons);
        // if (!state.player.hasPlayerClubSeasons) {
        //   payload.teamClass = 1;
        // } else {
        //   const storedSelectedSeason = window.localStorage.getItem('seasonSelected');
        //   if (Object.keys(playerInfoAndSeasons.seasons).includes(storedSelectedSeason)) {
        //     payload.teamClass = 0;
        //   } else if (Object.keys(playerInfoAndSeasons.national_seasons).includes(storedSelectedSeason)) {
        //     payload.teamClass = 1;
        //     commit('filterGeneral/setIsPlayerNationalTeam', true, { root: true });
        //   }
        // }
        // if (state.playerTeamClass !== payload.teamClass) {
        //   commit('updatePlayerTeamClass', payload.teamClass);
        // }
        // await dispatch(
        //   'filterGeneral/updateSeasons',
        //   { seasons: state.playerSeasons[payload.teamClass] },
        //   { root: true },
        // );
      }
      if (seasonPlayers) {
        // commit('updateSeasonPlayers', seasonPlayers);
      }
      if (seasonTeams) {
        const teamsMap = seasonTeams.reduce((accumulator, team) => {
          accumulator.set(team.team_id, team);
          return accumulator;
        }, new Map());
        if (rootState.teamsGeneral.seasonTeams.length === 0) {
          commit('teamsGeneral/updateTeams', seasonTeams, { root: true });
        }
        commit('filterGeneral/setSeasonTeamsMap', teamsMap, { root: true });
        commit('teamsGeneral/updateTeamsForAnalysis', seasonTeams, { root: true });
      }
      if (seasonGames) {
        const gamesMap = seasonGames.reduce((accumulator, game) => {
          game.seasonType = seasonWithGames.season_type || 0;
          game.dateTimeMillis = new Date(game.date_time_utc).valueOf();
          accumulator.set(game.game_id, game);
          return accumulator;
        }, new Map());
        commit('filterGeneral/setSeasonGamesMap', gamesMap, { root: true });
        commit('gamesGeneral/updateSeasonGames', seasonGames, { root: true });
      }
      commit('leagueGeneral/updateSeasonPlays', seasonPlays, { root: true });
      const filteredGoalPlays = filterByCategoryType(data_combination, playerGoalPlayIndexes());
      commit('playersOffensive/updateGoalPlaysStack', filteredGoalPlays.offensive, { root: true });
      commit('playersDefensive/updateGoalPlaysStack', filteredGoalPlays.defensive, { root: true });
      commit('playersPossession/updatePlaysStack', posession, { root: true });
      commit('playersOffensive/updatePlaysStack', offensive, { root: true });
      commit('playersDefensive/updatePlaysStack', defensive, { root: true });
      commit('setIsSectionLoading', false, { root: true });
    } catch (e) {
      // commit('setIsSectionLoading', false, { root: true });
      return Promise.reject(e);
    }
  },
  /**
   * @author Alejandro
   * @description This function fill the statistic data for player, is triggered by the action 'checkState'
   * or by the view Player.vue when a season changes or the team changes from club to national
   * @param dispatch
   * @param commit
   * @param state
   * @param rootGetters
   * @param {number} teamClass - 1 for national team and 0 for club
   * @param {Object} payload
   * @return {Promise<never>}
   */
  async fillStatisticData(
    { dispatch, commit, state, rootGetters, rootState },
    payload = { teamClass: 0, promises: [undefined] },
  ) {
    try {
      state.loadCount += 1;
      if (!rootState.isSectionLoading) commit('setIsSectionLoading', true, { root: true });
      payload.promises.push(dispatch('getPlayersBySeason', true));
      payload.promises.push(dispatch('teamsGeneral/getTeamsBySeasonForAnalysis', true, { root: true }));
      payload.promises.push(
        dispatch('gamesGeneral/fetchGamesBySeason', rootGetters['filterGeneral/getSeasonId'], { root: true }),
      );
      payload.promises.push(dispatch('leagueGeneral/getSeasonPlays', true, { root: true }));
      payload.promises.push(
        getGoalsPlayerBySeason({
          seasonId: rootGetters['filterGeneral/getSeasonId'],
          playerId: state.player.player_id,
        }),
      );
      payload.promises.push(
        getPlayerStatisticsBySeason({
          seasonId: rootGetters['filterGeneral/getSeasonId'],
          playerId: state.player.player_id,
        }),
      );
      const [
        playerInfoAndSeasons,
        seasonPlayers,
        seasonTeams,
        seasonWithGames,
        seasonPlays,
        {
          data: { data_combination },
        },
        {
          data: {
            data: { posession, offensive, defensive },
          },
        },
      ] = await Promise.all(payload.promises);
      const seasonGames = seasonWithGames.games || [];
      if (playerInfoAndSeasons) {
        playerInfoAndSeasons.seasons = Object.entries(playerInfoAndSeasons.seasons).reduce((accumulator, [k, v]) => {
          if (v.show_videos) {
            accumulator[k] = v;
          }
          return accumulator;
        }, {});
        playerInfoAndSeasons.national_seasons = Object.entries(playerInfoAndSeasons.national_seasons).reduce(
          (accumulator, [k, v]) => {
            if (v.show_videos) {
              accumulator[k] = v;
            }
            return accumulator;
          },
          {},
        );
        playerInfoAndSeasons.hasPlayerClubSeasons = !!Object.keys(playerInfoAndSeasons.seasons).length;
        commit('filterGeneral/setHasPlayerClubSeasons', playerInfoAndSeasons.hasPlayerClubSeasons, {
          root: true,
        });
        commit('filterGeneral/setHasPlayerNationalTeam', playerInfoAndSeasons.is_national_team, { root: true });
        commit('filterGeneral/setTeamId', playerInfoAndSeasons.team_id, { root: true });
        commit(
          'mediaGeneral/setSummaryData',
          {
            isTeam: false,
            subjectId: playerInfoAndSeasons.player_id,
            teamId: playerInfoAndSeasons.team_id,
            positionId: playerInfoAndSeasons.position_id,
          },
          { root: true },
        );
        commit('updatePlayerSeasons', {
          teamClass: 0,
          seasons: getFormattedSeasons({ ...playerInfoAndSeasons.seasons }),
        });
        commit('updatePlayerSeasons', {
          teamClass: 1,
          seasons: getFormattedSeasons({ ...playerInfoAndSeasons.national_seasons }),
        });
        const playerTeam = seasonTeams.find(team => team.team_id === playerInfoAndSeasons.team_id);
        if (playerTeam) {
          playerInfoAndSeasons.teamName = playerTeam.colloquial_name || playerTeam.team_name;
        }
        commit('updatePlayer', playerInfoAndSeasons);
        if (!state.player.hasPlayerClubSeasons) {
          payload.teamClass = 1;
          commit('filterGeneral/setIsPlayerNationalTeam', true, { root: true });
        } else {
          const storedSelectedSeason = window.localStorage.getItem('seasonSelected');
          if (Object.keys(playerInfoAndSeasons.seasons).includes(storedSelectedSeason)) {
            payload.teamClass = 0;
          } else if (Object.keys(playerInfoAndSeasons.national_seasons).includes(storedSelectedSeason)) {
            payload.teamClass = 1;
            commit('filterGeneral/setIsPlayerNationalTeam', true, { root: true });
          }
        }
        if (state.playerTeamClass !== payload.teamClass) {
          commit('updatePlayerTeamClass', payload.teamClass);
        }
        await dispatch(
          'filterGeneral/updateSeasons',
          { seasons: state.playerSeasons[payload.teamClass] },
          { root: true },
        );
      }
      if (seasonPlayers) {
        commit('updateSeasonPlayers', seasonPlayers);
      }
      if (seasonTeams) {
        const teamsMap = seasonTeams.reduce((accumulator, team) => {
          accumulator.set(team.team_id, team);
          return accumulator;
        }, new Map());
        if (rootState.teamsGeneral.seasonTeams.length === 0) {
          commit('teamsGeneral/updateTeams', seasonTeams, { root: true });
        }
        commit('filterGeneral/setSeasonTeamsMap', teamsMap, { root: true });
        commit('teamsGeneral/updateTeamsForAnalysis', seasonTeams, { root: true });
      }
      if (seasonGames) {
        const gamesMap = seasonGames.reduce((accumulator, game) => {
          game.seasonType = seasonWithGames.season_type || 0;
          game.dateTimeMillis = new Date(game.date_time_utc).valueOf();
          accumulator.set(game.game_id, game);
          return accumulator;
        }, new Map());
        commit('filterGeneral/setSeasonGamesMap', gamesMap, { root: true });
        commit('gamesGeneral/updateSeasonGames', seasonGames, { root: true });
      }
      commit('leagueGeneral/updateSeasonPlays', seasonPlays, { root: true });
      const filteredGoalPlays = filterByCategoryType(data_combination, playerGoalPlayIndexes());
      commit('playersOffensive/updateGoalPlaysStack', filteredGoalPlays.offensive, { root: true });
      commit('playersDefensive/updateGoalPlaysStack', filteredGoalPlays.defensive, { root: true });
      commit('playersPossession/updatePlaysStack', posession, { root: true });
      commit('playersOffensive/updatePlaysStack', offensive, { root: true });
      commit('playersDefensive/updatePlaysStack', defensive, { root: true });
      commit('setIsSectionLoading', false, { root: true });
    } catch (e) {
      // commit('setIsSectionLoading', false, { root: true });
      return Promise.reject(e);
    }
  },
  /**
   * @author Alejandro
   * @param {Object} context
   * @param {Object} payload
   * @param {number} payload.seasonId
   * @param {number} payload.playerId
   */
  async getPlayerStatisticsBySeason(context, payload) {
    try {
      const response = await getPlayerStatisticsBySeason(payload);
      context.commit('playersPossession/updatePlaysStack', response.data.data.posession, { root: true });
      context.commit('playersOffensive/updatePlaysStack', response.data.data.offensive, { root: true });
      context.commit('playersDefensive/updatePlaysStack', response.data.data.defensive, { root: true });
    } catch (e) {
      return Promise.reject(e);
    }
  },
  /**
   * @param {Object} context
   * @param {Object} payload
   * @param {number} payload.seasonId
   * @param {number} payload.playerId
   * @return {Promise}
   */
  async getPlayerInfo({ commit }, payload) {
    try {
      const playerInfo = await getPlayerInfo(payload);
      commit('updatePlayer', playerInfo);
    } catch (e) {
      return Promise.reject(e);
    }
  },
  /**
   * @author Alejandro
   * @param {Object} context
   * @param {Object} payload
   * @param {(number|string)} payload.teamId
   * @param {boolean} payload.showLoading
   */
  async getPlayersBySeasonAndTeam({ commit }, { teamId, showLoading }) {
    try {
      let seasonSelected = window.localStorage.getItem('seasonSelected');
      if (seasonSelected) {
        seasonSelected = Number(seasonSelected);
      }
      if (showLoading) commit('setIsSectionLoading', true, { root: true });
      const seasonId = seasonSelected;
      const response = await getPlayersBySeasonAndTeam({ seasonId, teamId });
      if (showLoading) commit('setIsSectionLoading', false, { root: true });
      return response.data[0].players;
    } catch (e) {
      // commit('setIsSectionLoading', false, { root: true });
      return Promise.reject(e);
    }
  },

  async getPlayersBySeason({ /*state,*/ commit, rootGetters }, returnData = false) {
    try {
      const seasonId = rootGetters['filterGeneral/getSeasonId'];
      // if (state.lastSeasonForSeasonPlayers === seasonId && state.lastSeasonPlayers.length) {
      //   return state.lastSeasonPlayers;
      // }
      commit('updateLastSeasonForSeasonPlayers', seasonId);
      const { data } = await getPlayersBySeasonAndTeam({ seasonId });
      if (returnData) {
        return data;
      }
      commit('updateSeasonPlayers', data);
    } catch (e) {
      return Promise.reject(e);
    }
  },

  async getPlayersForPlayersView({ dispatch, commit }, { teamId, name }) {
    // console.log('LLEgo aqui PARA PEDIR LOS JUGADORES DE UN EQUIPO');
    // if (state.lastTeamForPlayersView !== teamId) {
    commit('updatePlayersForPlayersView', []);
    const players = await dispatch('getPlayersBySeasonAndTeam', {
      teamId: teamId,
      showLoading: true,
    });

    commit('updatePlayersForPlayersView', players);
    commit('updateTeamForPlayersView', teamId);
    commit('updateTeamNameForPlayersView', name);
  },
  // },
};

const getters = {
  player(state) {
    return state.player;
  },
  lastTeamPlayersView(state) {
    return state.lastTeamForPlayersView;
  },
  lastTeamNamePlayersView(state) {
    return state.lastTeamNameForPlayersView;
  },
  playerId() {
    return state.player.player_id;
  },
  teamId() {
    return state.player.team_id;
  },
  seasonPlayers(state) {
    return state.seasonPlayers;
  },
  getPlaysGoal(state, rootState, rootGetters) {
    const sections = rootGetters.filterGeneral.sections;
    const results = rootGetters.filterGeneral.results;
    const playingAs = rootGetters.filterGeneral.playingAs;
    const categoryType = rootGetters.filterGeneral.categoryType;
    return state.playsGoal.filter(play => {
      return (
        // TODO: player_id estático hasta recibir de la API.
        //play[2] === state.player.player_id &&
        play[2] === 2002 &&
        play[5] === categoryType &&
        sections.includes(play[7]) &&
        results.includes(play[8]) &&
        (playingAs === null ? true : play[9] === playingAs)
      );
    });
  },
  /**
   * @author José Rafael Gutierrez
   * @param state
   * @param rootState
   * @param rootGetters
   * @return Array
   */
  getPlays: (state, rootState, rootGetters) => {
    const sections = rootGetters.filterGeneral.sections;
    const results = rootGetters.filterGeneral.results;
    const playingAs = rootGetters.filterGeneral.playingAs;
    const categoryType = rootGetters.filterGeneral.categoryType;
    return state.plays.filter(play => {
      return (
        // TODO: player_id estático hasta recibir de la API.
        //play[2] === state.player.player_id &&
        play[2] === 2002 &&
        play[5] === categoryType &&
        sections.includes(play[7]) &&
        results.includes(play[8]) &&
        (playingAs === null ? true : play[9] === playingAs)
      );
    });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
